import { SourceHolder } from './SourceProviderFactory'
import { useArtifactStore } from '@/store/artifacts'
import { useLogsStore } from '@/store/logs'
import router from '@/router'
import { humanReadableTimestamp } from '@/composables/datetime'
import i18n from '@/plugins/i18n'

const { t } = i18n.global

export type ArtifactSourceData = {
  artifactId: string
  artifactType: string
  artifactTimestamp: number
}

export abstract class GenericArtifactSourceHolder<T extends ArtifactSourceData> extends SourceHolder<T> {
  readonly supportedHeights: number[] = [20, 50, 150, 270, 540, 800, 1080]
  abstract readonly artifactType: string

  async loadSourceData(signal: AbortSignal): Promise<T | null> {
    if (signal.aborted) return null
    const artifactStore = useArtifactStore()
    const artifactId = await artifactStore.getLatestArtifactForCameraAndArtifactTypeWithTimestamp(
      this.cameraId,
      this.artifactType,
      this.timestamp,
      1000 * 60 * 60 * 24
    )
    if (!artifactId) return null
    const artifact = artifactStore.getArtifact(artifactId)
    if (!artifact) return null
    return {
      artifactId: artifact.artifactId,
      artifactType: artifact.type,
      artifactTimestamp: artifact.timestamp,
    } as T
  }

  async loadSource(height: number, signal: AbortSignal, isInView: boolean): Promise<string | null> {
    if (signal.aborted) return null
    const artifactStore = useArtifactStore()
    let source: string | null = null
    try {
      source = await artifactStore.loadArtifactPayload(this.sourceData!.artifactId, signal, height, isInView)
    } catch (e) {
      useLogsStore().addLogEntry({
        level: 'ERROR',
        message: `Error loading artifact ${this.sourceData!.artifactId} for camera ${this.cameraId} at timestamp ${this.timestamp} and height ${height}`,
        error: e instanceof Error ? e : String(e),
        tag: 'canvas',
      })
      return null
    }
    if (signal.aborted) return null
    return source
  }

  ondblclick() {
    router
      .push({
        name: 'Artifact',
        params: {
          globalUniqueIdentifier: router.currentRoute.value.params.globalUniqueIdentifier,
          artifactId: this.sourceData?.artifactId,
        },
      })
      .catch(e => {
        useLogsStore().addLogEntry({
          level: 'ERROR',
          message: `Error navigating to artifact view for artifact ${this.sourceData?.artifactId}`,
          error: e,
          tag: 'canvas',
        })
      })
  }
  onContextMenu() {}

  canShowTooltip(): boolean {
    return !!this.sourceData
  }

  getTooltipText(): string {
    if (!this.canShowTooltip()) return ''
    return `${t('artifact.type.' + this.artifactType)} (${this.camera.name}), ${humanReadableTimestamp(this.sourceData!.artifactTimestamp, false)}`
  }
}
